/**
 * W1-11b (app-shell-ui): the UI service (`services.ui`) and the UiBridge implementation (ARCHITECTURE.md §3.2, §6.2;
 * DESIGN.md §4.4, §4.19, §5.14, §8.4).
 *
 * `createUiService({ services })` returns a `ShellUiService`: the contract's `UiService` plus a small external store
 * the shell's React parts subscribe to (toasts, the dialog stack, gesture hints, palette, cheat sheet, context menu,
 * panel / focus requests, Laptop mini preview and drawer, dock tabs).
 *
 *  - Toasts: info / success / warning / error with an optional Undo link (runs Edit > Undo while the step the toast
 *    describes is still the last one: the history position is recorded when it is raised) or command action;
 *    a repeat of the visible toast counts up instead of stacking; at most MAX_TOASTS are kept (oldest dropped).
 *    Timers live in the Toasts component (paused while hovered or focused). A toast that only repeats a live
 *    on-screen notice is dropped (`NOTICE_COVERED_TOASTS`, UX-002), so the first-project storage warning does not
 *    stack on the banner/chip that already says it.
 *  - Dialogs: `openDialog(id, props)` mounts the registered DialogDef in the DialogHost and resolves with its typed
 *    result; dismissal, `closeDialog(id)`, re-opening the same id and unmounting resolve `null`. An id nobody
 *    registered resolves `null` at once with a "not available yet" toast. Popover-kind dialogs get an anchor (the
 *    focused element, else the last pointer position); every entry records its opener (the focused element), where
 *    focus returns when it closes. `confirm` / `choose` are the host's own system dialogs (dismissal resolves
 *    `false` / `null`). `dismissDialogs()` answers them all as dismissed (the DialogHost calls it when the open
 *    project closes or is replaced).
 *  - Context menus remember the event that opened them (`CommandContext.event` of the commands run from them).
 *  - Banner dismissals (storage health, the export reminder) last until the user has edited for another 2 hours
 *    (`dismissBanner`, `noteEditing`; DESIGN.md §5.13 "reminders every 2 hours of editing"). Editing time is the time
 *    between committed changes, each gap counted up to EDIT_GAP_CAP_MS (a pause is not editing).
 *  - `presetChanged(preset)`: whether the layout preset in effect differs from the one recorded last in this session
 *    (the workspace layout opens a preset's dock tab once when the preset takes effect, not on every remount).
 *  - `noteMouseUse(command)`: after TIP_AFTER_USES mouse uses of a command that has a shortcut, one first-use tip
 *    ("... also has a shortcut: K") per command, ever (remembered in settings `ext['shell.tipsShown']`), only while
 *    settings.general.firstUseTips is on.
 *  - `showPanel(id)` switches to the panel's workspace, selects its dock tab and asks the layout to reveal it (Laptop
 *    drawer / mini preview); `focusRegion(slot)` does the same and focuses the slot's region afterwards.
 *
 * `createUiBridge({ services, when })` is the bridge the design-system primitives use (labels and bindings from the
 * keymap, enabled state and run through the registry, translation, and `describe` for menu items).
 *
 * Wiring (W1-16 services.ts): `const ui = createUiService({ services: () => services })`, then `ui.install(window)`
 * once at startup (main.tsx does it) so popover anchors know the pointer.
 */

import type { Json, MessageRef, Unsubscribe } from '../core/util/types';
import type { StorageHealth } from '../persist/types';
import type { DocStore } from '../state/types';
import type { UiBridge } from '../ui/types';
import type { DialogId, DialogMap } from './dialogs/types';
import { contextMenuBeatF64 } from './contextMenuAt';
import type {
  AppServices,
  Chord,
  CommandContext,
  CommandId,
  CommandSource,
  MenuId,
  PanelDef,
  PanelSlot,
  Registry,
  ToastOptions,
  ToastHandle,
  UiService,
  WhenContext,
} from './registry/types';
import { commandChecked, commandEnabled, currentWhen, ref } from './shell/commandState';
import { workspaceOfSlot } from './shell/LayoutPresets';
import { m } from './shell/shell.messages';

export type PalettePrefix = '>' | '#' | '@' | '=' | '?' | ':';
/** The event a command was triggered by (CommandContext.event). */
export type TriggerEvent = NonNullable<CommandContext['event']>;
export type ToastKind = NonNullable<ToastOptions['kind']>;

/** The history step an undo-toast describes: the open project's store and the seq of its last applied entry. */
export interface ToastUndoTarget {
  readonly store: DocStore;
  readonly seq: number;
}

export interface ToastEntry {
  readonly id: number;
  readonly message: MessageRef;
  readonly kind: ToastKind;
  readonly undo: boolean;
  /**
   * For an undo-toast: the step it describes (recorded when it is raised, right after the commit). Its Undo link is
   * offered only while that step is still the one Edit > Undo would undo; null when nothing could be undone then.
   */
  readonly undoTarget: ToastUndoTarget | null;
  readonly action: NonNullable<ToastOptions['action']> | null;
  readonly durationMs: number;
  /** How many times the same toast was raised while visible ("x3"). */
  readonly count: number;
  /** Bumped on every repeat (restarts the timer). */
  readonly revision: number;
}

export interface DialogAnchor {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export type SystemDialog =
  | {
      readonly kind: 'confirm';
      readonly title: MessageRef;
      readonly body: MessageRef;
      readonly confirm: MessageRef;
      readonly danger: boolean;
    }
  | {
      readonly kind: 'choose';
      readonly title: MessageRef;
      readonly body: MessageRef | null;
      readonly options: ReadonlyArray<{
        readonly value: string;
        readonly label: MessageRef;
        readonly description?: MessageRef;
      }>;
    };

/** One open dialog: a registered DialogDef (`id`) or a system dialog (`system`). */
export interface DialogEntry {
  /** Unique per opening. */
  readonly key: number;
  readonly id: DialogId | null;
  readonly props: unknown;
  readonly system: SystemDialog | null;
  /** Where a popover-kind dialog points (viewport px). */
  readonly anchor: DialogAnchor | null;
  /** The element that had focus when the dialog opened (focus returns there when it closes). */
  readonly opener: Element | null;
}

export interface PaletteState {
  readonly open: boolean;
  readonly prefix: PalettePrefix;
  /** Bumped on every `openPalette` (the palette resets its input). */
  readonly nonce: number;
}

export interface GestureHint {
  readonly chord: Chord;
  readonly label: MessageRef;
}

export interface UiSnapshot {
  readonly toasts: readonly ToastEntry[];
  readonly dialogs: readonly DialogEntry[];
  readonly gestureHints: readonly GestureHint[] | null;
  readonly palette: PaletteState;
  readonly cheatSheet: boolean;
  /** Last `showPanel`: the layout reveals it (dock tab, drawer, mini preview). */
  readonly panelRequest: { readonly id: string; readonly slot: PanelSlot; readonly nonce: number } | null;
  /** Last `focusRegion`: the layout makes the slot visible, then focus moves into it. */
  readonly focusRequest: { readonly slot: PanelSlot; readonly nonce: number } | null;
  /** Laptop layout (Full): the floating mini preview is shown. */
  readonly miniPreview: boolean;
  /** Laptop layout: the right drawer (dock + inspector) is open. */
  readonly drawer: boolean;
  /** Context menu opened at a viewport position. */
  readonly contextMenu: {
    readonly menu: MenuId;
    readonly x: number;
    readonly y: number;
    readonly nonce: number;
    /** The right click (or Menu key) that opened it: commands run from the menu get it as `ctx.event`. */
    readonly event: TriggerEvent | null;
    /** Beat under the opener (CommandContext.at), when one could be read. */
    readonly at: number | null;
  } | null;
  /** Active tab per tabbed slot (panel id). */
  readonly activeTabs: Readonly<Partial<Record<PanelSlot, string>>>;
  /**
   * Banners the user dismissed (they stay away across projects and remounts until BANNER_REMIND_MS of editing has
   * passed since the dismissal).
   */
  readonly dismissedBanners: readonly string[];
}

export interface ShellUiService extends UiService {
  getSnapshot(): UiSnapshot;
  subscribe(cb: () => void): Unsubscribe;
  dismissToast(id: number): void;
  /** Called by the DialogHost: the dialog with this key finished (`null` = dismissed). */
  resolveDialog(key: number, result: unknown): void;
  closePalette(): void;
  setCheatSheet(open: boolean): void;
  setMiniPreview(open: boolean): void;
  setDrawer(open: boolean): void;
  openContextMenu(menu: MenuId, x: number, y: number, event?: TriggerEvent | null): void;
  closeContextMenu(): void;
  /** Answer every open dialog as dismissed (`null`; confirm `false`): the project they belong to went away. */
  dismissDialogs(): void;
  /** Hide a dismissible banner until the user has edited for BANNER_REMIND_MS more. */
  dismissBanner(id: string): void;
  /** A change was committed at `at` (epoch ms): counts editing time and brings back banners whose reminder is due. */
  noteEditing(at?: number): void;
  /** Editing time counted so far in this session (ms). */
  editingMs(): number;
  /** Records the layout preset in effect; true when it differs from the previous one recorded in this session. */
  presetChanged(preset: string): boolean;
  setActiveTab(slot: PanelSlot, panel: string): void;
  /** Track the pointer (popover anchors); returns the uninstall function. */
  install(win: Window): () => void;
  /** Resolve every open dialog with null and drop the toasts (project closed, tests). */
  reset(): void;
}

export const MAX_TOASTS = 4;
/** Dismissed banners come back after this much editing time (DESIGN.md §5.13: reminders every 2 hours of editing). */
export const BANNER_REMIND_MS = 2 * 60 * 60 * 1000;
/** A gap between two committed changes counts as editing time up to this long. */
export const EDIT_GAP_CAP_MS = 60_000;
/** Mouse uses of a command before its first-use tip (DESIGN.md §5.14: a frequent action done by mouse 5 times). */
export const TIP_AFTER_USES = 5;
/** settings ext key: commands whose first-use tip was shown. */
export const TIPS_EXT = 'shell.tipsShown';
/** settings ext key: active dock tab per slot. */
export const TABS_EXT = 'shell.activeTabs';

const DEFAULT_DURATION: Readonly<Record<ToastKind, number>> = {
  info: 4000,
  success: 4000,
  warning: 6500,
  error: 9000,
};

/** Every panel slot (showPanel searches them all). */
export const PANEL_SLOTS: readonly PanelSlot[] = [
  'chart.tools',
  'chart.timeline',
  'chart.preview',
  'chart.dock',
  'chart.inspector',
  'timing.left',
  'timing.main',
  'timing.bottom',
  'publish.left',
  'publish.right',
  'topbar.left',
  'topbar.center',
  'topbar.right',
  'statusbar',
];

/** A registered panel by id (any slot). */
export function findPanel(registry: Registry, id: string): PanelDef | undefined {
  for (const slot of PANEL_SLOTS) {
    const p = registry.panels(slot).find((x) => x.id === id);
    if (p !== undefined) return p;
  }
  return undefined;
}

/**
 * Toast message keys that only repeat an on-screen notice (UX-002, CR-W3-01-7): the same words are already in the
 * banner row or the save chip while the notice is live, so the toast is dropped instead of stacking a duplicate.
 * Keys are namespace-qualified message keys; the value names the storage notice that already says it. A toast is
 * covered while the notice is live (`persisted: false` for `notPersisted`), which is also the moment the wizard
 * raises its first-project warning — before the banner row has mounted.
 */
export const NOTICE_COVERED_TOASTS: ReadonlyMap<string, 'notPersisted'> = new Map([
  ['wizard.persistRefused', 'notPersisted'],
]);

function sameRef(a: MessageRef, b: MessageRef): boolean {
  if (a.key !== b.key) return false;
  const pa = a.params ?? {};
  const pb = b.params ?? {};
  const ka = Object.keys(pa);
  if (ka.length !== Object.keys(pb).length) return false;
  return ka.every((k) => pa[k] === pb[k]);
}

function stringList(v: Json | undefined): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function tabsOf(v: Json | undefined): Partial<Record<PanelSlot, string>> {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return {};
  const out: Partial<Record<PanelSlot, string>> = {};
  for (const [k, x] of Object.entries(v as { readonly [k: string]: Json })) {
    if (typeof x === 'string' && (PANEL_SLOTS as readonly string[]).includes(k)) out[k as PanelSlot] = x;
  }
  return out;
}

export interface UiServiceDeps {
  /** The app services (lazy: the UI service is one of them). Null before they exist. */
  readonly services: () => AppServices | null;
  /** Schedules a DOM step after the next paint (default: two animation frames). */
  readonly afterPaint?: (fn: () => void) => void;
  /** Developer diagnostics (default console.warn). */
  readonly report?: (message: string) => void;
}

const INITIAL: UiSnapshot = {
  toasts: [],
  dialogs: [],
  gestureHints: null,
  palette: { open: false, prefix: '>', nonce: 0 },
  cheatSheet: false,
  panelRequest: null,
  focusRequest: null,
  miniPreview: true,
  drawer: false,
  contextMenu: null,
  activeTabs: {},
  dismissedBanners: [],
};

function defaultAfterPaint(fn: () => void): void {
  if (typeof requestAnimationFrame !== 'function') {
    setTimeout(fn, 0);
    return;
  }
  requestAnimationFrame(() => {
    requestAnimationFrame(fn);
  });
}

/** The last applied history entry of a store as an undo target (null at the bottom of the history). */
export function undoTargetOf(store: DocStore): ToastUndoTarget | null {
  const h = store.history;
  if (h.position < 0) return null;
  const entry = h.entries()[h.position];
  return entry === undefined ? null : { store, seq: entry.seq };
}

/** The undo-toast's step is still the one Edit > Undo would undo (same project, nothing done or undone since). */
export function undoTargetCurrent(target: ToastUndoTarget | null, store: DocStore | null): boolean {
  if (target === null || store === null || store !== target.store) return false;
  return undoTargetOf(store)?.seq === target.seq;
}

/** Test play or Theater covers the window (first-use tips wait; UX-013). */
export function fullWindowActive(s: AppServices): boolean {
  if (s.editor.getState().preview.theater) return true;
  try {
    const w = currentWhen(s);
    return w.has('testPlay') || w.has('theater');
  } catch {
    return false;
  }
}

export function createUiService(deps: UiServiceDeps): ShellUiService {
  const report =
    deps.report ??
    ((msg: string) => {
      console.warn(`ui: ${msg}`);
    });
  const afterPaint = deps.afterPaint ?? defaultAfterPaint;
  let snap: UiSnapshot = INITIAL;
  let tabsLoaded = false;
  const listeners = new Set<() => void>();
  const finishers = new Map<number, (result: unknown) => void>();
  const mouseUses = new Map<CommandId, number>();
  /** Editing time (ms) when each dismissed banner was dismissed. */
  const dismissedAt = new Map<string, number>();
  let editing = 0;
  let lastEditAt: number | null = null;
  let lastPreset: string | null = null;
  let seq = 0;
  let lastPointer: { x: number; y: number } | null = null;
  /** Last storage health seen (UX-002: whether the persistence notice is live); null until the client answers. */
  let health: StorageHealth | null = null;
  let healthWatched = false;

  /** Follow the persist client's health so `toast` can drop notices the banner row already shows. */
  const watchHealth = (): void => {
    if (healthWatched) return;
    try {
      const s = services();
      if (s === null) return;
      s.persist.onHealth((h) => {
        health = h;
      });
      healthWatched = true;
      void s.persist
        .health()
        .then((h) => {
          health = h;
        })
        .catch(() => undefined);
    } catch {
      // Services are not ready yet; the next call retries.
    }
  };

  /** The toast only repeats a live on-screen notice (its banner/chip already says it; UX-002). */
  const coveredByNotice = (msg: MessageRef): boolean => {
    // Every covered key so far maps to the `notPersisted` notice, which is live while the browser has not
    // promised persistence (`persisted` false or still unknown).
    return NOTICE_COVERED_TOASTS.has(msg.key) && health?.persisted !== true;
  };

  const set = (patch: Partial<UiSnapshot>): void => {
    snap = { ...snap, ...patch };
    for (const cb of [...listeners]) cb();
  };

  const services = (): AppServices | null => {
    try {
      return deps.services();
    } catch {
      return null;
    }
  };

  const ensureTabs = (): void => {
    if (tabsLoaded) return;
    const s = services();
    if (s === null) return;
    tabsLoaded = true;
    const stored = tabsOf(s.settings.getExt(TABS_EXT));
    // Silent: this runs inside getSnapshot (during a render); the new snapshot is picked up by its readers.
    if (Object.keys(stored).length > 0) snap = { ...snap, activeTabs: { ...stored, ...snap.activeTabs } };
  };

  /** The step Edit > Undo would undo now (the last applied history entry of the open project), or null. */
  const currentUndoTarget = (): ToastUndoTarget | null => {
    const store = services()?.store ?? null;
    if (store === null) return null;
    return undoTargetOf(store);
  };

  const removeToast = (id: number): void => {
    if (snap.toasts.some((t) => t.id === id)) set({ toasts: snap.toasts.filter((t) => t.id !== id) });
  };

  const toast = (msg: MessageRef, opts: ToastOptions = {}): ToastHandle => {
    watchHealth();
    if (coveredByNotice(msg))
      return {
        dismiss: () => undefined,
      };
    const kind = opts.kind ?? 'info';
    const undo = opts.undo === true;
    const action = opts.action ?? null;
    const durationMs = Math.max(
      1500,
      opts.durationMs ?? (undo || action !== null ? Math.max(6500, DEFAULT_DURATION[kind]) : DEFAULT_DURATION[kind]),
    );
    const undoTarget = undo ? currentUndoTarget() : null;
    const last = snap.toasts[snap.toasts.length - 1];
    if (last?.kind === kind && last.undo === undo && sameRef(last.message, msg) && action === null) {
      const repeated: ToastEntry = {
        ...last,
        undoTarget,
        count: last.count + 1,
        revision: last.revision + 1,
        durationMs,
      };
      set({ toasts: [...snap.toasts.slice(0, -1), repeated] });
      return {
        dismiss: () => {
          removeToast(repeated.id);
        },
      };
    }
    const entry: ToastEntry = {
      id: ++seq,
      message: msg,
      kind,
      undo,
      undoTarget,
      action,
      durationMs,
      count: 1,
      revision: 0,
    };
    set({ toasts: [...snap.toasts, entry].slice(-MAX_TOASTS) });
    return {
      dismiss: () => {
        removeToast(entry.id);
      },
    };
  };

  const removeDialog = (key: number): void => {
    if (!snap.dialogs.some((d) => d.key === key)) return;
    set({ dialogs: snap.dialogs.filter((d) => d.key !== key) });
  };

  const finish = (key: number, result: unknown): void => {
    const fn = finishers.get(key);
    finishers.delete(key);
    removeDialog(key);
    fn?.(result);
  };

  /** The focused element, or null when focus is nowhere in particular (the page body). */
  const focusedElement = (): Element | null => {
    const doc = typeof document === 'undefined' ? null : document;
    const active = doc?.activeElement ?? null;
    return active === null || doc === null || active === doc.body || active === doc.documentElement ? null : active;
  };

  const anchorNow = (): DialogAnchor => {
    const active = focusedElement();
    if (active !== null) {
      const r = active.getBoundingClientRect();
      if (r.width > 0 || r.height > 0) return { x: r.left, y: r.top, width: r.width, height: r.height };
    }
    if (lastPointer !== null) return { x: lastPointer.x, y: lastPointer.y, width: 0, height: 0 };
    const w = typeof window === 'undefined' ? 800 : window.innerWidth;
    const h = typeof window === 'undefined' ? 600 : window.innerHeight;
    return { x: w / 2, y: h / 3, width: 0, height: 0 };
  };

  const pushDialog = (entry: Omit<DialogEntry, 'key' | 'opener'>, onFinish: (result: unknown) => void): void => {
    const key = ++seq;
    finishers.set(key, onFinish);
    set({ dialogs: [...snap.dialogs, { ...entry, key, opener: focusedElement() }], contextMenu: null });
  };

  const dismissDialogs = (): void => {
    if (snap.dialogs.length === 0) return;
    const open = snap.dialogs;
    set({ dialogs: [] });
    for (const d of open) {
      const fn = finishers.get(d.key);
      finishers.delete(d.key);
      fn?.(null);
    }
  };

  const focusSlot = (slot: PanelSlot): void => {
    if (typeof document === 'undefined') return;
    const root = document.querySelector<HTMLElement>(`[data-slot="${slot}"]`);
    if (root === null) return;
    const region = root.matches('[data-region]') ? root : root.querySelector<HTMLElement>('[data-region]');
    const target = region ?? root;
    if (!target.hasAttribute('tabindex') && target.tabIndex < 0) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  const revealSlot = (s: AppServices | null, slot: PanelSlot): void => {
    const ws = workspaceOfSlot(slot);
    if (s === null || ws === null || s.store === null) return;
    if (s.editor.getState().workspace !== ws) s.nav.setWorkspace(ws);
  };

  const ui: ShellUiService = {
    getSnapshot: () => {
      ensureTabs();
      watchHealth();
      return snap;
    },
    subscribe(cb) {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    toast,
    dismissToast(id) {
      removeToast(id);
    },
    confirm(opts) {
      return new Promise<boolean>((resolve) => {
        pushDialog(
          {
            id: null,
            props: null,
            anchor: null,
            system: {
              kind: 'confirm',
              title: opts.title,
              body: opts.body,
              confirm: opts.confirm,
              danger: opts.danger === true,
            },
          },
          (r) => {
            resolve(r === true);
          },
        );
      });
    },
    choose<T extends string>(opts: {
      readonly title: MessageRef;
      readonly body?: MessageRef;
      readonly options: ReadonlyArray<{
        readonly value: T;
        readonly label: MessageRef;
        readonly description?: MessageRef;
      }>;
    }): Promise<T | null> {
      return new Promise<T | null>((resolve) => {
        const values = new Set<string>(opts.options.map((o) => o.value));
        pushDialog(
          {
            id: null,
            props: null,
            anchor: null,
            system: { kind: 'choose', title: opts.title, body: opts.body ?? null, options: opts.options },
          },
          (r) => {
            resolve(typeof r === 'string' && values.has(r) ? (r as T) : null);
          },
        );
      });
    },
    openDialog<K extends DialogId>(id: K, props: DialogMap[K]['props']): Promise<DialogMap[K]['result']> {
      const s = services();
      const def = s?.registry.dialog(id);
      if (def === undefined) {
        report(`dialog "${id}" is not registered`);
        toast(ref(m.toastDialogUnavailable), { kind: 'info' });
        return Promise.resolve(null as DialogMap[K]['result']);
      }
      // One instance per id: a second opening replaces the first (which resolves null).
      for (const d of snap.dialogs) if (d.id === id) finish(d.key, null);
      return new Promise<DialogMap[K]['result']>((resolve) => {
        pushDialog({ id, props, system: null, anchor: def.kind === 'popover' ? anchorNow() : null }, (r) => {
          resolve((r ?? null) as DialogMap[K]['result']);
        });
      });
    },
    closeDialog(id) {
      for (const d of snap.dialogs) if (d.id === id) finish(d.key, null);
    },
    resolveDialog(key, result) {
      finish(key, result);
    },
    setGestureHints(items) {
      set({ gestureHints: items === null || items.length === 0 ? null : [...items] });
    },
    showPanel(id) {
      const s = services();
      if (s === null) return;
      const panel = findPanel(s.registry, id);
      if (panel === undefined) {
        report(`showPanel: no panel "${id}"`);
        return;
      }
      revealSlot(s, panel.slot);
      const panelRequest = { id, slot: panel.slot, nonce: (snap.panelRequest?.nonce ?? 0) + 1 };
      if (panel.slot !== 'chart.dock' && panel.slot !== 'chart.inspector') {
        set({ panelRequest });
        return;
      }
      ensureTabs();
      const activeTabs = { ...snap.activeTabs, [panel.slot]: id };
      set({ panelRequest, activeTabs });
      s.settings.setExt(TABS_EXT, { ...activeTabs });
    },
    focusRegion(slot) {
      const s = services();
      revealSlot(s, slot);
      set({ focusRequest: { slot, nonce: (snap.focusRequest?.nonce ?? 0) + 1 } });
      afterPaint(() => {
        focusSlot(slot);
      });
    },
    noteMouseUse(command) {
      const s = services();
      if (s === null) return;
      if (!s.settings.value.general.firstUseTips) return;
      // Never over the full-window play surfaces (UX-013): the tip waits for a later use.
      if (fullWindowActive(s)) return;
      const n = (mouseUses.get(command) ?? 0) + 1;
      mouseUses.set(command, n);
      if (n < TIP_AFTER_USES) return;
      const shown = stringList(s.settings.getExt(TIPS_EXT));
      if (shown.includes(command)) return;
      const chord = s.keymap.bindingsFor(command)[0];
      const def = s.registry.command(command);
      if (chord === undefined || def === undefined) return;
      s.settings.setExt(TIPS_EXT, [...shown, command].slice(-500));
      toast(ref(m.toastTip, { command: s.i18n.t(def.title), keys: s.keymap.label(chord) }), {
        kind: 'info',
        durationMs: 9000,
      });
    },
    openPalette(prefix) {
      set({ palette: { open: true, prefix: prefix ?? '>', nonce: snap.palette.nonce + 1 }, contextMenu: null });
    },
    closePalette() {
      if (snap.palette.open) set({ palette: { ...snap.palette, open: false } });
    },
    setCheatSheet(open) {
      if (snap.cheatSheet !== open) set({ cheatSheet: open });
    },
    setMiniPreview(open) {
      if (snap.miniPreview !== open) set({ miniPreview: open });
    },
    setDrawer(open) {
      if (snap.drawer !== open) set({ drawer: open });
    },
    openContextMenu(menu, x, y, event) {
      const ev = event ?? null;
      const svcs = services();
      const at = svcs === null ? null : contextMenuBeatF64(svcs, ev);
      set({ contextMenu: { menu, x, y, nonce: (snap.contextMenu?.nonce ?? 0) + 1, event: ev, at } });
    },
    closeContextMenu() {
      if (snap.contextMenu !== null) set({ contextMenu: null });
    },
    dismissDialogs,
    dismissBanner(id) {
      dismissedAt.set(id, editing);
      if (!snap.dismissedBanners.includes(id)) set({ dismissedBanners: [...snap.dismissedBanners, id] });
    },
    noteEditing(at = Date.now()) {
      if (lastEditAt !== null) editing += Math.min(Math.max(0, at - lastEditAt), EDIT_GAP_CAP_MS);
      lastEditAt = at;
      const due = snap.dismissedBanners.filter((id) => editing - (dismissedAt.get(id) ?? 0) >= BANNER_REMIND_MS);
      if (due.length === 0) return;
      for (const id of due) dismissedAt.delete(id);
      set({ dismissedBanners: snap.dismissedBanners.filter((id) => !due.includes(id)) });
    },
    editingMs: () => editing,
    presetChanged(preset) {
      if (preset === lastPreset) return false;
      lastPreset = preset;
      return true;
    },
    setActiveTab(slot, panel) {
      ensureTabs();
      if (snap.activeTabs[slot] === panel) return;
      const activeTabs = { ...snap.activeTabs, [slot]: panel };
      set({ activeTabs });
      services()?.settings.setExt(TABS_EXT, { ...activeTabs });
    },
    install(win) {
      const onPointer = (e: PointerEvent | MouseEvent): void => {
        lastPointer = { x: e.clientX, y: e.clientY };
      };
      win.addEventListener('pointerdown', onPointer, true);
      win.addEventListener('contextmenu', onPointer, true);
      return () => {
        win.removeEventListener('pointerdown', onPointer, true);
        win.removeEventListener('contextmenu', onPointer, true);
      };
    },
    reset() {
      dismissDialogs();
      set({ toasts: [], gestureHints: null, contextMenu: null });
    },
  };
  return ui;
}

/** The shell UI behind `services.ui`, when it is one (tests may pass a plain fake). */
export function isShellUiService(ui: UiService): ui is ShellUiService {
  const x = ui as Partial<ShellUiService>;
  return (
    typeof x.getSnapshot === 'function' && typeof x.subscribe === 'function' && typeof x.resolveDialog === 'function'
  );
}

// ---------------------------------------------------------------------------------------------------------------
// UiBridge
// ---------------------------------------------------------------------------------------------------------------

export interface UiBridgeDeps {
  readonly services: () => AppServices;
  /** Current editor context (the keyboard router's); default: the registry's, else built from the services. */
  readonly when?: () => WhenContext;
  /** Source reported for runs (default 'button': mouse use, feeds first-use tips). */
  readonly source?: CommandSource;
}

/** The UiBridge of the app: keymap labels and bindings, registry state and runs, translation, command descriptions. */
export function createUiBridge(deps: UiBridgeDeps): UiBridge {
  const source = deps.source ?? 'button';
  const when = (s: AppServices): WhenContext => currentWhen(s, deps.when);
  return {
    label: (chord) => deps.services().keymap.label(chord),
    bindingsFor: (command) => deps.services().keymap.bindingsFor(command),
    enabled: (command) => {
      const s = deps.services();
      return commandEnabled(s, command, deps.when === undefined ? undefined : when(s));
    },
    run: (command, args) => {
      void deps.services().registry.runAny(command, args, source);
    },
    tr: (r) => deps.services().i18n.tr(r),
    describe: (command) => {
      const s = deps.services();
      const def = s.registry.command(command);
      if (def === undefined) return null;
      const checked = commandChecked(s, def, when(s));
      return {
        title: { key: def.title },
        ...(def.icon === undefined ? {} : { icon: def.icon }),
        ...(checked === undefined ? {} : { checked }),
      };
    },
  };
}
